import createClient from 'openapi-fetch';
import { getRequestEvent, isServer } from 'solid-js/web';
import type { paths } from './__generated__/database.ts';
import type { Client, ClientOptions as OpenApiClientOptions } from 'openapi-fetch';

type RequiredOptions = {
	platform: string;
	version: string;
	sessionId: string;
};

type ClientOptions = OpenApiClientOptions &
	RequiredOptions & {
		localStorageToken?: string;
	};

const clientOptions = {
	baseUrl: '/',
	credentials: 'include',
	mode: 'cors',
	referrerPolicy: 'origin',
	keepalive: true,
} satisfies Partial<ClientOptions>;

type ServerClientConfig = OpenApiClientOptions &
	RequiredOptions & {
		traceId: string;
	};

export type ApiClient = Client<paths, 'application/json'>;

export function createServerApiClient(config: ServerClientConfig, inputHeaders?: Record<string, string>): ApiClient {
	if (!isServer) {
		throw new Error('Cannot create server API client in the browser.');
	}

	const { platform, version, sessionId, traceId, ...rest } = config;

	const client: ApiClient = createClient<paths>({
		...clientOptions,
		...rest,
		headers: inputHeaders,
		fetch: globalThis.fetch,
	});

	client.use({
		async onRequest({ request: req }) {
			req.headers.set('x-session-id', sessionId);
			req.headers.set('x-troon-client-platform', platform);
			req.headers.set('x-troon-client-version', version);
			req.headers.set('x-trace-id', traceId);
			return req;
		},
	});

	return client as ApiClient;
}

export function createApiClient(config: ClientOptions) {
	let sessionId: string;
	try {
		sessionId = window.crypto.randomUUID();
	} catch {
		// missing randomUUID is only in unsupported browsers
	}

	const { localStorageToken, platform, version, ...rest } = config;
	const client = createClient<paths>({
		...clientOptions,
		...rest,
		fetch: globalThis.fetch,
	}) as ApiClient;
	client.use({
		async onRequest({ request: req }) {
			req.headers.set('x-session-id', sessionId);
			req.headers.set('x-troon-client-platform', platform);
			req.headers.set('x-troon-client-version', version);
			try {
				req.headers.set('x-trace-id', window.crypto.randomUUID());
			} catch {
				// missing randomUUID is only in unsupported browsers
			}

			return req;
		},
	});

	if (localStorageToken) {
		client.use({
			async onRequest({ request: req }) {
				try {
					const token = window.localStorage?.getItem(localStorageToken);
					if (token) {
						req.headers.set('Authorization', `Bearer ${token}`);
					}
				} catch {
					// don't blow up if local storage fails
				}
				return req;
			},
		});
	}

	globalThis.__trBrowserClient = client;
	return getBrowserClient();
}

export function getApiClient(config?: ClientOptions): ApiClient {
	if (isServer) {
		// @ts-expect-error They forgot about this in the upstream defs…
		return getRequestEvent()!.locals.apiClient;
	}

	const client = getBrowserClient();
	if (!client && config) {
		return createApiClient(config);
	}
	return client;
}

function getBrowserClient() {
	return globalThis.__trBrowserClient as ApiClient;
}

declare global {
	var __trBrowserClient: ApiClient;
}
